import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/use-auth";
import { useDebounce } from "@/hooks/use-debounce";
import { format } from "date-fns";
import { Plus, Search, Upload, MoreHorizontal } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { handleAuthError } from "@/utils/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PaginationControls } from "@/components/common/PaginationControl";
import { Skeleton } from "@/components/ui/skeleton";

interface Brand {
  id: number;
  brandCode: string;
  brandName: string;
  brandImageUri: string;
  brandDescription: string;
  createdDate: string;
  lastUpdatedDate: string;
  xssAttack: boolean;
}

interface BrandResponse {
  brandVOS: Brand[];
  totalRecords: number;
}

interface Column {
  header: string;
  accessorKey?: keyof Brand;
  id?: string;
  cell?: (row: Brand) => React.ReactNode;
}

interface BrandFormData {
  brandName: string;
  brandCode: string;
  brandDescription: string;
  brandLogo: File | null;
}

const BrandSchema = Yup.object().shape({
  brandName: Yup.string().required("Brand name is required"),
  brandCode: Yup.string().required("Brand code is required"),
  brandDescription: Yup.string(),
  brandLogo: Yup.mixed().nullable().optional(),
});

type BrandFormValues = Yup.InferType<typeof BrandSchema>;

const DELAY = 500;

const RewardPointBrands = () => {
  const navigate = useNavigate();
  const { refreshToken } = useAuth();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalBrands, setTotalBrands] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("createdDate");
  const [sortOrder, setSortOrder] = useState("desc");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState<Brand | null>(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [logo, setLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);

  const form = useForm<BrandFormValues>({
    resolver: yupResolver(BrandSchema),
    defaultValues: {
      brandName: "",
      brandCode: "",
      brandDescription: "",
      brandLogo: null,
    },
    mode: "onChange",
  });

  function debounce<T extends (...args: any[]) => void>(
    func: T,
    delay: number
  ) {
    return function (...args: Parameters<T>) {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      debounceTimeout.current = setTimeout(() => {
        func(...args);
        debounceTimeout.current = null;
      }, delay);
    };
  }

  const handleApiError = async (response: Response) => {
    return handleAuthError(response, refreshToken);
  };

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL}/brand/getBrands`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            pageNumber: currentPage,
            pageSize,
            brandName: debouncedSearchQuery,
            sortBy,
            sortOrder,
          }),
        }
      );

      const shouldRetry = await handleApiError(response);
      if (shouldRetry) {
        return fetchBrands();
      }

      if (!response.ok) {
        throw new Error("Failed to fetch brands");
      }

      const data: BrandResponse = await response.json();
      setBrands(data.brandVOS);
      setTotalBrands(data.totalRecords);
    } catch (error) {
      setError(error?.message || "Failed to fetch brands");
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = debounce((query: string) => {
    setCurrentPage(0);
    setSearchQuery(query);
  }, DELAY);

  useEffect(() => {
    fetchBrands();
  }, [currentPage, pageSize, debouncedSearchQuery, sortBy, sortOrder]);

  const handleEdit = (brand: Brand) => {
    setEditingBrand(brand);
    // Reset form first, then set values
    form.reset({
      brandName: brand.brandName,
      brandCode: brand.brandCode,
      brandDescription: brand.brandDescription,
      brandLogo: null,
    });
    setLogoPreview(
      brand.brandImageUri
        ? `${import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL}${
            brand.brandImageUri
          }`
        : null
    );
    setLogo(null);
    setCreateDialogOpen(true);
  };

  const handleDelete = async (brand: Brand) => {
    setBrandToDelete(brand);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!brandToDelete) return;

    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL
        }/brand/deleteBrandById?id=${brandToDelete.id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          credentials: "include",
        }
      );

      const shouldRetry = await handleApiError(response);
      if (shouldRetry) {
        return confirmDelete();
      }

      if (!response.ok) {
        throw new Error("Failed to delete brand");
      }

      toast.success("Brand deleted successfully");
      setDeleteDialogOpen(false);
      setBrandToDelete(null);
      fetchBrands();
    } catch (error) {
      toast.error(error?.message || "Failed to delete brand");
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogo(file);
      form.setValue("brandLogo", file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (formData: BrandFormValues) => {
    try {
      const formDataToSend = new FormData();

      // Only append logo if a new one is uploaded (logo is not null)
      // For updates, if user doesn't change the logo, logo will be null
      if (logo) {
        formDataToSend.append("brandLogo", logo);
      }

      const brandData = {
        id: editingBrand?.id,
        brandCode: formData.brandCode,
        brandName: formData.brandName,
        brandDescription: formData.brandDescription,
      };

      const blob = new Blob([JSON.stringify(brandData)], {
        type: "application/json",
      });
      formDataToSend.append("brandVO", blob);

      const response = await fetch(
        `${
          import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL
        }/brand/createOrUpdateBrand`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
          },
          body: formDataToSend,
          credentials: "include",
        }
      );

      const shouldRetry = await handleApiError(response);
      if (shouldRetry) {
        return onSubmit(formData);
      }

      if (!response.ok) {
        throw new Error("Failed to create/update brand");
      }

      toast.success(
        editingBrand
          ? "Brand updated successfully"
          : "Brand created successfully"
      );
      setCreateDialogOpen(false);
      form.reset();
      setLogo(null);
      setLogoPreview(null);
      setEditingBrand(null);
      fetchBrands();
    } catch (error) {
      console.error("Error in onSubmit:", error);
      toast.error(error?.message || "Failed to create/update brand");
    }
  };

  const columns: Column[] = [
    {
      header: "Created Date",
      accessorKey: "createdDate",
      cell: (row) => (
        <div className="font-medium">
          {format(new Date(row.createdDate), "MMM dd, yyyy, h:mm a")}
        </div>
      ),
    },
    {
      header: "Updated Date",
      accessorKey: "lastUpdatedDate",
      cell: (row) => (
        <div className="font-medium">
          {format(new Date(row.lastUpdatedDate), "MMM dd, yyyy, h:mm a")}
        </div>
      ),
    },
    {
      header: "Brand Logo",
      accessorKey: "brandImageUri",
      cell: (row) => (
        <div className="flex items-center">
          <img
            src={
              row.brandImageUri
                ? `${import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL}${
                    row.brandImageUri
                  }`
                : "/placeholder.svg"
            }
            alt={row.brandName}
            className="h-10 w-10 object-contain rounded-md"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/placeholder.svg";
            }}
          />
        </div>
      ),
    },
    {
      header: "Brand Name",
      accessorKey: "brandName",
      cell: (row) => <div className="font-medium">{row.brandName}</div>,
    },
    {
      header: "Brand Code",
      accessorKey: "brandCode",
      cell: (row) => <div className="font-medium">{row.brandCode}</div>,
    },
    {
      header: "Actions",
      id: "actions",
      cell: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-white min-w-[8rem] p-1"
          >
            <DropdownMenuItem onClick={() => handleEdit(row)}>
              View/Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleDelete(row)}
              className="text-red-600"
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  // Skeleton loading component
  const BrandsSkeleton = () => (
    <div className="space-y-6">
      {/* Header Skeleton */}
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-8 w-32 mb-2" />
          <Skeleton className="h-4 w-48" />
        </div>
        <Skeleton className="h-10 w-32" />
      </div>

      {/* Filters Skeleton */}
      <div className="flex flex-col sm:flex-row gap-4 items-end sm:items-center bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
        <div className="flex-1">
          <Skeleton className="h-4 w-20 mb-1" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>

      {/* Table Skeleton */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <Skeleton className="h-6 w-32" />
        </div>
        <div className="p-6">
          {/* Table Header Skeleton */}
          <div className="grid grid-cols-6 gap-4 mb-4">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-16" />
          </div>
          {/* Table Rows Skeleton */}
          {[...Array(5)].map((_, index) => (
            <div
              key={index}
              className="grid grid-cols-6 gap-4 py-4 border-b border-gray-100 last:border-b-0"
            >
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-8 w-8 rounded" />
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-8 w-8 rounded" />
            </div>
          ))}
        </div>
      </div>

      {/* Pagination Skeleton */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center space-x-2">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-8 w-16" />
        </div>
        <div className="flex items-center space-x-2">
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-16" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <BrandsSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Brands</h1>
          <p className="text-gray-500">Manage your loyalty program brands</p>
        </div>
        <Button
          onClick={() => {
            setEditingBrand(null);
            form.reset({
              brandName: "",
              brandCode: "",
              brandDescription: "",
              brandLogo: null,
            });
            setLogo(null);
            setLogoPreview(null);
            setCreateDialogOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-2" /> Add Brand
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-end sm:items-center bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
        <div className="flex-1">
          <label
            htmlFor="search"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Brand Name
          </label>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              id="search"
              placeholder="Search brands..."
              className="pl-9"
              value={searchQuery}
              onChange={(e) => {
                const query = e.target.value;
                setSearchQuery(query);
                debouncedSearch(query);
              }}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <DataTable title="All Brands" data={brands} columns={columns} />

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">Rows per page:</span>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => {
              setPageSize(Number(value));
              setCurrentPage(0);
            }}
          >
            <SelectTrigger className="w-[80px]">
              <SelectValue placeholder={pageSize.toString()} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <PaginationControls
          totalPages={Math.ceil(totalBrands / pageSize)}
          currentPage={currentPage}
          onNextHandler={() =>
            setCurrentPage((p) =>
              Math.min(Math.ceil(totalBrands / pageSize) - 1, p + 1)
            )
          }
          onPrevHandler={() => setCurrentPage((p) => Math.max(0, p - 1))}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>

      {/* Create/Edit Brand Sheet */}
      <Sheet
        open={createDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setEditingBrand(null);
            form.reset({
              brandName: "",
              brandCode: "",
              brandDescription: "",
              brandLogo: null,
            });
            setLogo(null);
            setLogoPreview(null);
          }
          setCreateDialogOpen(open);
        }}
      >
        <SheetContent className="sm:max-w-[500px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {editingBrand ? "Edit Brand" : "Create New Brand"}
            </SheetTitle>
            <SheetDescription>
              {editingBrand
                ? "Update brand information"
                : "Add a new brand to your loyalty program"}
            </SheetDescription>
          </SheetHeader>
          <div className="py-6">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                {/* Logo Upload */}
                <div className="space-y-2">
                  <FormLabel>Brand Logo</FormLabel>
                  <div className="flex items-center gap-4">
                    <div className="h-24 w-24 rounded-lg border-2 border-dashed border-gray-300 p-2">
                      {logoPreview ? (
                        <img
                          src={logoPreview}
                          alt="Brand logo preview"
                          className="h-full w-full object-contain rounded-md"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center">
                          <Upload className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="hidden"
                        id="logo-upload"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          document.getElementById("logo-upload")?.click()
                        }
                      >
                        Upload Logo
                      </Button>
                      <p className="text-sm text-gray-500 mt-1">
                        Recommended size: 200x200px
                      </p>
                    </div>
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="brandName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Brand Name*</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Nike" {...field} />
                      </FormControl>
                      <FormDescription>The name of your brand</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="brandCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Brand Code*</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. NIKE-001" {...field} />
                      </FormControl>
                      <FormDescription>
                        A unique identifier for the brand
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="brandDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Enter brand description..."
                          className="min-h-[100px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <SheetFooter className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setCreateDialogOpen(false);
                      setEditingBrand(null);
                      form.reset({
                        brandName: "",
                        brandCode: "",
                        brandDescription: "",
                        brandLogo: null,
                      });
                      setLogo(null);
                      setLogoPreview(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingBrand ? "Update Brand" : "Create Brand"}
                  </Button>
                </SheetFooter>
              </form>
            </Form>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              brand "{brandToDelete?.brandName}" and remove it from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RewardPointBrands;
